import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { observationTuning } from "./observationTuning";

export interface ObservationCamera {
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  frameObject: (object: THREE.Object3D) => void;
  resize: () => void;
}

export function createObservationCamera(
  canvas: HTMLCanvasElement,
  renderer: THREE.WebGLRenderer,
): ObservationCamera {
  const tuning = observationTuning.camera;
  const camera = new THREE.PerspectiveCamera(tuning.fieldOfView, 1, 0.015, 120);
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = tuning.dampingFactor;
  controls.enablePan = true;
  controls.screenSpacePanning = true;
  controls.panSpeed = tuning.panSpeed;
  controls.zoomToCursor = true;
  controls.rotateSpeed = tuning.rotateSpeed;
  controls.zoomSpeed = tuning.zoomSpeed;
  controls.minPolarAngle = Math.PI * 0.06;
  controls.maxPolarAngle = Math.PI * 0.94;

  function frameObject(object: THREE.Object3D): void {
    const bounds = new THREE.Box3().setFromObject(object);
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) * 0.5;
    const fitDistance = radius / Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));

    controls.target.copy(center);
    camera.position.copy(center).add(new THREE.Vector3(fitDistance * 0.72, fitDistance * 0.38, fitDistance * 0.92));
    camera.near = Math.max(radius * 0.008, 0.01);
    camera.far = Math.max(radius * 40, 80);
    controls.minDistance = Math.max(radius * tuning.minimumDistanceInRadii, camera.near * 8);
    controls.maxDistance = fitDistance * tuning.maximumDistanceInFitDistances;
    camera.updateProjectionMatrix();
    controls.update();
  }

  function resize(): void {
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (width === 0 || height === 0) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  return { camera, controls, frameObject, resize };
}
